import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { getConfig } from '@/runtime-config';
import { isLiveDemoSession } from '@/utility/liveDemo';
import { useMediaQuery } from './useMediaQuery';
import { useTourContext } from '@/context/useTourContext';
import type { TourVariant } from './tours/types';

const STORAGE_KEY = 'KollabHR:tour';

function readSeenTours(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<
      string,
      boolean
    >;
  } catch {
    return {};
  }
}

function markTourSeen(tourId: string) {
  const record = readSeenTours();
  record[tourId] = true;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
}

export function useTour<TMock extends Record<string, unknown>>(
  variant: TourVariant<TMock>,
  options?: {
    enabled?: boolean;
    tableTargetKey?: string;
    autoStart?: boolean;
    registerTrigger?: boolean;
    label?: string;
  }
) {
  // `enabled` lets a cross-route tour hosted above page level (see
  // AssetTourProvider) gate the header trigger to the routes it spans, while
  // `run`/`mockState` persist across navigation. `autoStart` narrows the
  // one-time auto-start further (e.g. only the tour's entry route).
  // `registerTrigger: false` skips the default header registration for hosts
  // that register their own labeled entries (see AssetTourProvider).
  const active = options?.enabled ?? true;
  const tableTargetKey = options?.tableTargetKey;
  const autoStartEnabled = options?.autoStart ?? true;
  const registerTrigger = options?.registerTrigger ?? true;
  const labelKey = options?.label;
  const tourEnabled = getConfig().ENABLE_TOUR && !isLiveDemoSession();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [run, setRun] = useState(false);
  const [skipBeacon, setSkipBeacon] = useState(false);
  const [mockState, setMockState] = useState<TMock>(variant.initialMockState);
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const startPathRef = useRef(window.location.pathname);

  const { t } = useTranslation(TranslationNS.Tour);
  const { registerTour } = useTourContext();

  const steps = useMemo(
    () => variant.buildSteps(setMockState, t),
    [variant, setMockState, t]
  );

  useEffect(() => {
    if (!tourEnabled || !isDesktop || !active || !autoStartEnabled) return;
    if (readSeenTours()[variant.tourId]) return;
    startPathRef.current = window.location.pathname;
    // First-time visit: show the beacon on the first step and wait for the
    // user to click it before the tooltip opens (see TourGuide's skipBeacon prop).
    setSkipBeacon(false);
    setStepIndex(null);
    setRun(false);
    requestAnimationFrame(() => setRun(true));
  }, [variant.tourId, tourEnabled, isDesktop, active, autoStartEnabled]);

  const onFinish = useCallback(() => {
    const finalize = () => {
      markTourSeen(variant.tourId);
      setRun(false);
      setStepIndex(null);
      setMockState(variant.initialMockState);
    };
    if (variant.onFinish) {
      variant.onFinish(startPathRef.current, finalize);
    } else {
      finalize();
    }
  }, [variant]);

  // For a tour hosted above page level (see AssetTourProvider): if the user
  // navigates away from the pages it spans while a step's target is still
  // being resolved, Joyride treats the vanished target as TARGET_NOT_FOUND
  // and auto-advances to the next step — silently running that step's
  // onEnter (often a navigate() of its own) on top of whatever the user was
  // actually doing. Unlike onFinish, this doesn't mark the tour as seen or
  // run variant.onFinish's own navigate — it just abandons the in-progress
  // run so it can autostart cleanly next time the user genuinely visits.
  const stopTour = useCallback(() => {
    setRun(false);
    setStepIndex(null);
    setMockState(variant.initialMockState);
  }, [variant.initialMockState]);

  const startTour = useCallback(() => {
    startPathRef.current = window.location.pathname;
    // Manual restart (e.g. header "Take tour" button): open the tooltip
    // immediately, skipping the beacon.
    setSkipBeacon(true);
    // Clear any stale active step from a previous run.
    setStepIndex(null);

    setRun(false);
    requestAnimationFrame(() => setRun(true));
  }, []);

  // Register startTour into context so the header can trigger it — skip when
  // tour is disabled or on mobile.
  useEffect(() => {
    if (!tourEnabled || !isDesktop || !active || !registerTrigger) return;
    return registerTour({
      id: variant.tourId,
      label: labelKey ? t(labelKey) : t('guide.take_tour'),
      start: startTour,
    });
  }, [
    registerTour,
    startTour,
    variant.tourId,
    t,
    tourEnabled,
    isDesktop,
    active,
    registerTrigger,
    labelKey,
  ]);

  const activeTarget = useMemo(() => {
    if (stepIndex == null) return null;
    const target = steps[stepIndex]?.target;
    return typeof target === 'string' ? target : null;
  }, [stepIndex, steps]);

  const isTableStep = useMemo(() => {
    if (!tableTargetKey || !activeTarget) return false;
    return activeTarget.includes(tableTargetKey);
  }, [activeTarget, tableTargetKey]);

  return {
    steps,
    run,
    skipBeacon,
    onFinish,
    startTour,
    stopTour,
    mockState,
    activeTarget,
    isTableStep,
    onStepChange: setStepIndex,
  };
}
